import { createRESTStore } from "@stores/shared/createRESTStore";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";
import useUserPreferencesStore from "@stores/shared/useUserPreferencesStore";

// there's no persisting of asset filter for now
// may add it later

const initialCollectionConfig = {
  page: 0,
  pageLength: 10,
};

const useAssetStore = createRESTStore({
  entityName: "assets",
  apiPaths: {
    fetch: API.BASE + API.FRONTEND.ASSETS,
    get: API.BASE + API.FRONTEND.ASSETS,
    update: API.BASE + API.BACKEND.ASSET_VERSIONS,
  },
  initialState: null,

  additionalActions: (set, get) => ({
    initializeAssets: async (path, type, rootId) => {
      set((state) => ({
        ...state,
        currentPath: path,
        currentType: type,
        currentId: path ? path.split("-").pop() : null,
        currentRootId: rootId,
        itemData: null,
        itemNodes: {},
        assets: null,
        config: initialCollectionConfig,
        pageView: null,
        ...(!path ? { currentFilter: "all", assetFilterData: null } : {}),
      }));

      const view = await get().loadPageView();
      set({ pageView: view });
    },

    fetchAssets: async (rootPath, includeChildren = false) => {
      set({ fetching: true });
      try {
        const apiUrl =
          get().apiPaths.fetch +
          "/" +
          rootPath +
          "/children/includeChildren/" +
          includeChildren;
        const result = await apiClient.get(apiUrl);
        // apiClient wraps response in { data: {...} }
        const assets = result.data.assets;
        set({ assets, fetching: false });
        return assets;
      } catch (error) {
        set({ fetching: false });
        console.error("useAssetStore.fetchAssets - Error:", error);
        return null;
      }
    },

    handleAssetFilter: async (filter) => {
      set((state) => ({
        ...state,
        currentFilter: filter,
      }));
      if (filter !== "all") {
        await get().fetchAssetFilterData(filter);
      }
    },

    handleAssetFilterClose: () => {
      set((state) => ({
        ...state,
        currentFilter: "all",
        assetFilterData: null,
        filterConfig: null,
      }));
    },

    fetchAssetFilterData: async (filter) => {
      set({ fetchingFilterData: true });
      try {
        const result = await apiClient.post(
          get().apiPaths.fetch + "/rolodex/" + filter,
          { ...initialCollectionConfig, data: parseInt(get().currentRootId) }, // sending root id to make sure filtering is done only within the root
        );
        set({
          assetFilterData: result.data.items,
          filterConfig: result.data.rolodex,
          fetchingFilterData: false,
        });
      } catch (error) {
        set({ fetchingFilterData: false });
        console.log(error);
      }
    },

    // Matches the (page, append) signature useInfiniteScroll's setPage calls with.
    // append=true appends the page to assetFilterData (scroll-triggered lazy load);
    // append=false (or a direct call) replaces it, e.g. for a future page jump.
    setFilterPage: async (page, append = false) => {
      const { currentFilter } = get();
      if (currentFilter === "all") return;

      set({ fetchingMoreFilterData: append, fetchingFilterData: !append });
      try {
        const result = await apiClient.post(
          get().apiPaths.fetch + "/rolodex/" + currentFilter,
          { ...initialCollectionConfig, page, data: parseInt(get().currentRootId) },
        );
        set((state) => ({
          assetFilterData: append
            ? [...(state.assetFilterData || []), ...result.data.items]
            : result.data.items,
          filterConfig: result.data.rolodex,
          fetchingFilterData: false,
          fetchingMoreFilterData: false,
        }));
      } catch (error) {
        set({ fetchingFilterData: false, fetchingMoreFilterData: false });
        console.log(error);
      }
    },

    getAssetItem: async (path) => {
      set({ fetchingItem: true });
      try {
        const response = await get().getItem(path);

        if (response) {
          set((state) => ({
            ...state,
            itemData: response,
            itemNodes: {
              ...state.itemNodes,
              [path]: response.children || [], // Default to empty array if children don't exist
            },
          }));
          return response;
        } else {
          console.log(`Error: Asset item not found for path: ${path}`);
          set((state) => ({
            ...state,
            itemData: null, // Resetting itemData if not found
            itemNodes: {
              ...state.itemNodes,
              [path]: [], // Resetting nodes for that path
            },
          }));
          return null;
        }
      } catch (error) {
        console.error("Error fetching asset item:", error);
        set((state) => ({
          itemData: null,
          itemNodes: { ...state.itemNodes, [path]: [] },
        })); // Reset itemData and nodes on error
      } finally {
        set({ fetchingItem: false }); // Ensure fetchingItem is reset after the operation
      }
    },

    updateAsset: async (
      id,
      data,
      successCallback,
      errorCallback,
      afterUpdate,
    ) => {
      set({ updating: true });
      try {
        const result = await apiClient.put(
          get().apiPaths.update + "/" + id,
          data,
        );
        set({ updating: false });
        successCallback();
        afterUpdate?.(result);
      } catch (error) {
        errorCallback(error);
        set({ updating: false });
      }
    },

    updateItemFavourite: (assetPath, isFav) => {
      set((state) => {
        const updates = {};

        if (state.itemData && state.itemData.path === assetPath) {
          updates.itemData = { ...state.itemData, isFavourite: isFav };
        }

        return updates;
      });
    },

    loadPageView: async () => {
      const sessionView = sessionStorage.getItem("assetView");
      if (sessionView) {
        set({ pageView: sessionView });
        return sessionView;
      }
      try {
        const { getPreference } = useUserPreferencesStore.getState();
        const apiView = await getPreference("assetView", null);
        if (apiView === "grid" || apiView === "list") {
          set({ pageView: apiView });
          sessionStorage.setItem("assetView", apiView);
          return apiView;
        }
      } catch (err) {
        console.error("Error fetching user preferences:", err);
      }
      set({ pageView: "grid" });
      return "grid";
    },

    setPageView: async (newView) => {
      set({ pageView: newView });
      sessionStorage.setItem("assetView", newView);
      try {
        const { setPreference } = useUserPreferencesStore.getState();
        await setPreference("assetView", newView);
      } catch (err) {
        console.error("Error updating user preferences:", err);
      }
    },
  }),
});

export default useAssetStore;
